/**
 * Les panneaux posés sur le monde.
 *
 * Toutes les fonctions sont pures : elles rendent des chaînes, ne touchent
 * jamais au document, et se testent sans navigateur. C'est le même partage que
 * pour la géométrie — ce qui se décide se teste, ce qui s'affiche se regarde.
 *
 * ## Pourquoi ce sont des panneaux et non des sections
 *
 * Une section pousse le canvas vers le bas et fait défiler ; un panneau
 * **flotte au-dessus** et laisse la vue occuper l'écran. C'est toute la
 * différence entre un document qui contient une illustration et un lieu où l'on
 * se trouve, et c'était le reproche : trop de texte, la vue reléguée au milieu.
 *
 * Le texte n'a pas disparu pour autant. Il est passé dans un tiroir, qu'on
 * ouvre quand on veut lire — pas avant.
 */

import { escape, escapeAttribute, formatNumber } from "./format.js";
import { fr } from "../i18n/fr.js";

const AU = 1.496e11;
const SOLAR_MASS = 1.989e30;
const EARTH_MASS = 5.972e24;
const YEAR = 31557600;
const DAY = 86400;

export type PanelKind = "star" | "planet" | "probe";

export interface ScenarioTab {
  readonly id: string;
  readonly label: string;
  readonly summary: string;
}

export interface EntityRow {
  readonly id: string;
  readonly label: string;
  readonly kind: PanelKind;
}

export interface InspectorView {
  readonly id: string;
  readonly label: string;
  readonly kind: PanelKind;
  /** En kilogrammes. */
  readonly mass: number;
  /** En mètres, depuis le barycentre du monde. */
  readonly distance: number;
  /** En mètres par seconde. */
  readonly speed: number;
  /** Rayon vrai, en mètres. */
  readonly radius: number;
  readonly following: boolean;
  readonly vx?: number;
  readonly vy?: number;
}

// ── Mise en forme ────────────────────────────────────────────────────────────

/**
 * Masse lisible : en masses terrestres tant que c'est raisonnable, sinon
 * solaires. Personne ne lit 1,989 × 10³⁰ kg.
 */
export function formatMass(kilograms: number): string {
  if (kilograms >= 0.05 * SOLAR_MASS) return `${formatNumber(kilograms / SOLAR_MASS, 2)} M☉`;
  return `${formatNumber(kilograms / EARTH_MASS, 2)} M⊕`;
}

/** Distance lisible : en unités astronomiques, l'échelle des orbites. */
export function formatDistance(metres: number): string {
  return `${formatNumber(metres / AU, 3)} ua`;
}

/**
 * Rayon lisible, en kilomètres.
 *
 * Pas en unités astronomiques : un rayon planétaire y vaudrait 0,000 et le
 * chiffre ne dirait plus rien. Ce n'est pas la même échelle que les orbites, et
 * c'est précisément ce que le monde permet maintenant de traverser.
 */
export function formatRadius(metres: number): string {
  if (metres >= 1e6) return `${grouped(formatNumber(metres / 1000, 0))} km`;
  if (metres >= 1000) return `${formatNumber(metres / 1000, 1)} km`;
  return `${formatNumber(metres, 0)} m`;
}

/**
 * Sépare les milliers par une espace fine insécable, à la française.
 *
 * Réservé aux rayons, et pour une raison précise : ce sont les seuls nombres de
 * l'application à dépasser cinq chiffres entiers. « 696000 km » ne se lit pas
 * d'un coup d'œil, « 696 000 km » si. Partout ailleurs, les grandeurs sont
 * ramenées à une unité qui les garde courtes, et une séparation serait du bruit.
 */
function grouped(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function formatSpeed(metresPerSecond: number): string {
  return `${formatNumber(metresPerSecond / 1000, 2)} km/s`;
}

/**
 * Durée simulée, dans l'unité qui la rend lisible.
 *
 * Ce temps est **compté**, jamais lu sur une horloge : c'est le nombre de pas
 * multiplié par leur durée (CLAUDE.md §5).
 */
export function formatElapsed(seconds: number): string {
  if (seconds >= YEAR) return `${formatNumber(seconds / YEAR, 2)} ans`;
  if (seconds >= DAY) return `${formatNumber(seconds / DAY, 1)} jours`;
  return `${formatNumber(seconds / 3600, 1)} h`;
}

const SUPERSCRIPTS = ["⁰", "¹", "²", "³", "⁴", "⁵", "⁶", "⁷", "⁸", "⁹"] as const;

function superscript(exponent: number): string {
  const sign = exponent < 0 ? "⁻" : "";
  const digits = String(Math.abs(exponent))
    .split("")
    .map((digit) => SUPERSCRIPTS[Number(digit)] ?? digit)
    .join("");
  return `${sign}${digits}`;
}

/**
 * Dérive d'énergie, en notation scientifique dès qu'elle est petite.
 *
 * À six décimales fixes, une dérive de 1,2·10⁻⁷ s'affiche « 0,000000 » : le
 * seul chiffre qui dise si l'intégrateur tient devient invisible, et une
 * simulation qui part en vrille ressemble à une simulation parfaite. Un test
 * l'a attrapé — le nombre est trop important pour être arrondi jusqu'à
 * disparaître.
 */
export function formatDrift(value: number): string {
  if (value === 0) return "0";
  if (Math.abs(value) >= 1e-3) return formatNumber(value, 6);

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const mantissa = value / Math.pow(10, exponent);
  return `${formatNumber(mantissa, 1)}·10${superscript(exponent)}`;
}

function kindLabel(kind: PanelKind): string {
  if (kind === "star") return fr.worldKindStar;
  if (kind === "planet") return fr.worldKindPlanet;
  return fr.worldKindProbe;
}

// ── La barre des scénarios ───────────────────────────────────────────────────

export function renderScenarioBar(
  scenarios: readonly ScenarioTab[],
  activeId: string,
): string {
  const buttons = scenarios
    .map((one) =>
      [
        `<button type="button" class="verlet-chip verlet-scenario" data-scenario="${escapeAttribute(one.id)}"`,
        ` title="${escapeAttribute(one.summary)}"`,
        ` aria-describedby="verlet-scenario-${escapeAttribute(one.id)}-summary"`,
        ` aria-pressed="${one.id === activeId ? "true" : "false"}">`,
        `<span class="verlet-scenario__mark" aria-hidden="true">✓</span>`,
        escape(one.label),
        `</button>`,
        `<span class="verlet-sr-only" id="verlet-scenario-${escapeAttribute(one.id)}-summary">`,
        `${escape(one.summary)}</span>`,
      ].join(""),
    )
    .join("");

  return [
    `<div class="verlet-chips" role="group" aria-label="${escapeAttribute(fr.scenariosLabel)}">`,
    buttons,
    `</div>`,
  ].join("");
}

// ── Les analyses ─────────────────────────────────────────────────────────────

/**
 * Ce que la coquille sait d'une analyse : son nom, sa question, et si elle
 * confronte des mesures. Rien d'autre — ni ombre, ni gnomon, ni pesanteur.
 */
export interface AnalysisTab {
  readonly id: string;
  readonly label: string;
  readonly question: string;
  readonly confronts: boolean;
}

/**
 * L'entrée principale — l'analyse qui confronte, **nommée par sa question**.
 *
 * « Analyses » nomme un tiroir ; « La Terre est-elle plate ? » nomme ce qu'on
 * va y trouver. Le libellé vient de l'analyse elle-même : ce module ne connaît
 * aucune question, et n'en écrit aucune.
 */
export function renderFeaturedAnalysis(tab: AnalysisTab): string {
  return [
    `<button type="button" class="verlet-chip verlet-featured" data-featured="${escapeAttribute(tab.id)}"`,
    ` title="${escapeAttribute(fr.analysisFeaturedSummary)}"`,
    ` aria-describedby="verlet-featured-summary">`,
    escape(tab.question),
    `</button>`,
    `<span class="verlet-sr-only" id="verlet-featured-summary">`,
    `${escape(fr.analysisFeaturedSummary)}</span>`,
  ].join("");
}

/**
 * Les onglets du tiroir, **chacun disant s'il confronte des mesures**.
 *
 * Les trois se ressemblaient, et rien ne distinguait celle qui peut trancher
 * de celles qui expliquent pourquoi elles ne le peuvent pas. Marquer seulement
 * la première aurait laissé les autres muettes, et une absence ne se lit pas
 * (CLAUDE.md §7) : les deux états portent donc leur mention, en toutes lettres
 * et non par une couleur (CLAUDE.md §3).
 */
export function renderAnalysisTabs(tabs: readonly AnalysisTab[]): string {
  return tabs
    .map((tab) =>
      [
        `<button type="button" class="verlet-chip verlet-analysis-tab" data-analysis="${escapeAttribute(tab.id)}"`,
        ` id="verlet-analysis-tab-${escapeAttribute(tab.id)}" role="tab"`,
        ` aria-selected="false" aria-controls="verlet-analysis-panel" tabindex="-1">`,
        `<span class="verlet-analysis-tab__name">${escape(tab.label)}</span>`,
        `<span class="verlet-analysis-tab__badge">`,
        escape(tab.confronts ? fr.analysisConfrontsMeasures : fr.analysisWithoutMeasures),
        `</span>`,
        `</button>`,
      ].join(""),
    )
    .join("");
}

// ── La liste des corps ───────────────────────────────────────────────────────

export function renderEntityList(rows: readonly EntityRow[], selectedId: string): string {
  const items = rows
    .map((row) =>
      [
        `<li>`,
        `<button type="button" class="verlet-entity" data-entity="${escapeAttribute(row.id)}"`,
        ` data-kind="${escapeAttribute(row.kind)}"`,
        ` aria-pressed="${row.id === selectedId ? "true" : "false"}">`,
        // La nature est écrite, pas seulement dessinée : rien ne doit se lire à
        // la seule couleur (CLAUDE.md §3).
        `<span class="verlet-entity__name">${escape(row.label)}</span>`,
        `<span class="verlet-entity__kind">${escape(kindLabel(row.kind))}</span>`,
        `</button>`,
        `</li>`,
      ].join(""),
    )
    .join("");

  return [
    `<h2 class="verlet-panel__title" id="verlet-entities-title">`,
    `${escape(fr.worldEntitiesTitle)}</h2>`,
    `<ul class="verlet-entities">${items}</ul>`,
    `<div class="verlet-panel__actions">`,
    `<button type="button" class="verlet-chip" data-add>${escape(fr.worldAddPlanet)}</button>`,
    `<button type="button" class="verlet-chip" data-reset>${escape(fr.worldReset)}</button>`,
    `</div>`,
    // La mise en garde vit ici, au pied de la liste des corps, et non en
    // travers du bas de l'écran où elle passait sous le bandeau de commande.
    `<details class="verlet-panel__details">`,
    `<summary>${escape(fr.worldScaleDetails)}</summary>`,
    `<p class="verlet-panel__caveat">${escape(fr.worldCaveat)}</p>`,
    `</details>`,
  ].join("");
}

// ── L'inspecteur ─────────────────────────────────────────────────────────────

/**
 * Le curseur de masse porte l'**exposant décimal**, pas la masse.
 *
 * D'une lune à une étoile il y a huit ordres de grandeur : sur un curseur
 * linéaire, tout ce qui n'est pas une étoile se tasserait sur le premier pixel
 * et deviendrait inatteignable.
 */
export const MASS_EXPONENT_MIN = 18;
export const MASS_EXPONENT_MAX = 31;

export function renderInspector(view: InspectorView | undefined): string {
  const title = [
    `<h2 class="verlet-panel__title" id="verlet-inspector-title">`,
    `${escape(fr.worldInspectorTitle)}</h2>`,
  ].join("");

  if (view === undefined) {
    return `${title}<p class="verlet-panel__empty">${escape(fr.worldNoSelection)}</p>`;
  }

  const exponent = Math.log10(view.mass);

  return [
    title,
    `<p class="verlet-inspector__name">${escape(view.label)}`,
    `<span class="verlet-entity__kind">${escape(kindLabel(view.kind))}</span></p>`,
    `<dl class="verlet-inspector__facts">`,
    `<dt>${escape(fr.worldMassLabel)}</dt><dd data-fact="mass">${escape(formatMass(view.mass))}</dd>`,
    `<dt>${escape(fr.worldColumnDistance)}</dt>`,
    `<dd data-fact="distance">${escape(formatDistance(view.distance))}</dd>`,
    `<dt>${escape(fr.worldColumnSpeed)}</dt><dd data-fact="speed">${escape(formatSpeed(view.speed))}</dd>`,
    `<dt>${escape(fr.worldColumnRadius)}</dt><dd data-fact="radius">${escape(formatRadius(view.radius))}</dd>`,
    `</dl>`,
    `<label class="verlet-field">`,
    `<span class="verlet-field__label">${escape(fr.worldMassLabel)}</span>`,
    `<input type="range" data-mass min="${String(MASS_EXPONENT_MIN)}"`,
    ` max="${String(MASS_EXPONENT_MAX)}" step="0.01" value="${String(exponent)}"`,
    ` aria-valuetext="${escapeAttribute(formatMass(view.mass))}"`,
    ` aria-describedby="masse-aide">`,
    `<span class="verlet-field__hint" id="masse-aide">${escape(fr.worldMassHint)}</span>`,
    `</label>`,
    `<form class="verlet-motion" data-motion>`,
    `<h3>${escape(fr.velocityTitle)}</h3>`,
    `<label>${escape(fr.velocitySpeed)}<input name="speed" type="number" min="0" max="1000" step="any" required value="${(Math.hypot(view.vx ?? 0, view.vy ?? 0) / 1000).toFixed(2)}"></label>`,
    `<label>${escape(fr.velocityAngle)}<input name="angle" type="number" min="-360" max="360" step="any" required value="${(Math.atan2(view.vy ?? 0, view.vx ?? 0) * 180 / Math.PI).toFixed(2)}"></label>`,
    `<p>${escape(fr.velocityHint)}</p>`,
    `<div class="verlet-panel__actions"><button type="submit" class="verlet-chip">${escape(fr.velocityApply)}</button>`,
    `<button type="button" class="verlet-chip" data-stop>${escape(fr.velocityStop)}</button></div></form>`,
    `<div class="verlet-panel__actions">`,
    `<button type="button" class="verlet-chip" data-follow`,
    ` aria-pressed="${view.following ? "true" : "false"}">`,
    escape(view.following ? fr.worldFollowing : fr.worldFollowLabel),
    `</button>`,
    `<button type="button" class="verlet-chip" data-remove>${escape(fr.worldRemove)}</button>`,
    `</div>`,
  ].join("");
}

// ── Le bandeau de commande ───────────────────────────────────────────────────

export interface HudView {
  readonly running: boolean;
  readonly stepsPerFrame: number;
  /** Temps simulé, en secondes. */
  readonly elapsed: number;
  /** Dérive relative de l'énergie depuis le départ, sans dimension. */
  readonly drift: number;
}

export function renderHud(view: HudView): string {
  return [
    `<button type="button" class="verlet-chip" data-play`,
    ` aria-pressed="${view.running ? "true" : "false"}">`,
    escape(view.running ? fr.worldPause : fr.worldPlay),
    `</button>`,
    `<label class="verlet-hud__field">`,
    `<span>${escape(fr.worldSpeedLabel)}</span>`,
    `<input type="range" data-speed min="1" max="240" step="1"`,
    ` value="${String(view.stepsPerFrame)}" title="${escapeAttribute(fr.worldSpeedHint)}"`,
    ` aria-valuetext="${escapeAttribute(fr.worldSpeedValue(view.stepsPerFrame))}"`,
    ` aria-describedby="verlet-speed-help">`,
    `<output data-speed-readout>${escape(fr.worldSpeedValue(view.stepsPerFrame))}</output>`,
    `<output class="verlet-mobile-speed" data-mobile-speed>${escape(fr.mobileSpeedValue(view.stepsPerFrame))}</output>`,
    `<span class="verlet-sr-only" id="verlet-speed-help">${escape(fr.worldSpeedHint)}</span>`,
    `</label>`,
    `<p class="verlet-hud__readout">`,
    `<span>${escape(fr.worldElapsedLabel)}</span>`,
    `<output data-elapsed>${escape(formatElapsed(view.elapsed))}</output>`,
    `</p>`,
    `<p class="verlet-hud__readout" title="${escapeAttribute(fr.worldDriftHint)}">`,
    `<span>${escape(fr.worldDriftLabel)}</span>`,
    `<output data-drift>${escape(formatDrift(view.drift))}</output>`,
    `<span class="verlet-sr-only">${escape(fr.worldDriftHint)}</span>`,
    `</p>`,
    `<p class="verlet-hud__hint" id="verlet-world-help">${escape(fr.worldHint)}</p>`,
  ].join("");
}
